import { FC, MouseEvent, useRef, useState } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { JabraIcon, useJabraTheme } from '@gn-audio/jss-components';

import Drawer from 'common/components/Drawer/Drawer';
import { JabraSlider, JabraSliderRef } from 'common/components/JabraSlider/JabraSlider';
import { PillsSliderNavigation } from 'common/components/JabraSlider/PillsSliderNavigation';
import { BentoGridItem } from 'composables/Product/BentoGrid/types';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

import BentoGridSlide from './BentoGridSlide';

import styles from './BentoGridComponent.module.scss';

export interface BentoGridProps {
  items: BentoGridItem[];
  trackingSpotName?: string;
}

export const BentoGridComponent: FC<BentoGridProps> = ({ items, trackingSpotName }) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const spotRef = useSpotTracking(trackingSpotName);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);

  const { darkTheme } = useJabraTheme() ?? { darkTheme: false };
  const jabraSlider = useRef<JabraSliderRef>(null);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const grid = gridRef.current;
    if (!grid) return;

    grid.querySelectorAll('.grid-item').forEach((gridItem: HTMLDivElement) => {
      const rect = gridItem.getBoundingClientRect(),
        x = e.clientX - rect.left,
        y = e.clientY - rect.top;
      gridItem.style.setProperty('--mouse-x', `${x}px`);
      gridItem.style.setProperty('--mouse-y', `${y}px`);
    });
  };

  const handleMoveToSlide = (index: number) => {
    jabraSlider.current?.goTo(index);
  };

  const handleOpenItem = (index: number) => {
    setShowDrawer(true);
    setStartIndex(index);
  };

  const activeItem = items[activeIndex];
  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName}
      className="bg-theme"
      data-testid="bento-grid-container"
    >
      <div
        className={classNames(
          'container grid auto-rows-fr gap-6 lg:grid-cols-3',
          styles.featureGrid,
          darkTheme ? styles.darkGrid : styles.lightGrid
        )}
        onMouseMove={handleMouseMove}
        ref={gridRef}
        data-testid="bento-grid"
      >
        {items.map((item, index) => {
          const big = (index - 1) % 4 === 0 || (index - 2) % 4 === 0;
          const hasIconDropListItem = !!item.iconDropList?.value;
          const hasIconImage = !!item.icon.value?.src;

          return (
            <button
              className={classNames(
                'grid-item grid-item h-[300px]',
                big ? 'lg:col-span-2' : '',
                styles.featureGridItem
              )}
              onClick={() => handleOpenItem(index)}
              key={item.id}
              aria-describedby={`description_${item.id}`}
              aria-labelledby={`title_${item.id}`}
              data-testid={`bento-grid-item-${index}`}
            >
              <div role="presentation" className={styles.featureGridBorder} />
              <div role="presentation" className={styles.featureGridBackground} />

              <div className="absolute left-0 top-0 z-10 flex h-full w-full flex-col items-center justify-center bg-transparent p-4">
                {hasIconDropListItem ? (
                  <JabraIcon
                    className="mb-6 h-16 w-16 text-black dark:text-white"
                    Icon={icons[item.iconDropList?.value as keyof typeof icons]}
                    data-testid={`bento-grid-icon-${index}`}
                  />
                ) : (
                  hasIconImage && (
                    <Image
                      className="mb-6 h-16 w-16"
                      field={item.icon}
                      loading="lazy"
                      data-testid={`bento-grid-image-${index}`}
                    />
                  )
                )}
                <div
                  id={`title_${item.id}`}
                  className="heading-4"
                  dangerouslySetInnerHTML={{ __html: sanitize(item.title.value as string) }}
                />

                <div
                  id={`description_${item.id}`}
                  className="paragraph-large"
                  dangerouslySetInnerHTML={{ __html: sanitize(item.description.value as string) }}
                />
              </div>
            </button>
          );
        })}

        <Drawer
          title={activeItem?.title.value}
          description={activeItem?.description.value}
          size="md"
          open={showDrawer}
          close={() => setShowDrawer(false)}
          dialogContentClassName="md:min-w-[40rem]"
          data-testid="bento-grid-drawer"
        >
          <div className="flex flex-1 flex-col justify-between gap-11 pt-11">
            <JabraSlider
              mainClassName="w-full"
              startIndex={startIndex}
              slideClassName="basis-full md:px-12  px-6"
              showSlideArrows={false}
              ref={jabraSlider}
              onChange={setActiveIndex}
            >
              {items.map((gridItem) => (
                <BentoGridSlide key={gridItem.id} gridItem={gridItem} />
              ))}
            </JabraSlider>
            <PillsSliderNavigation
              activeIndex={activeIndex}
              total={items.length}
              goTo={handleMoveToSlide}
              className="flex w-full justify-end px-6 md:px-12"
            />
          </div>
        </Drawer>
      </div>
    </div>
  );
};
